import { config } from '../runtime-config';

/** Error carrying the HTTP status so pages can distinguish 404 / 400 / 500. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type TokenProvider = () => Promise<string | undefined>;
let tokenProvider: TokenProvider = async () => undefined;

/** Registered once by the auth layer so requests can attach a bearer token. */
export function setTokenProvider(fn: TokenProvider): void {
  tokenProvider = fn;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await tokenProvider();
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  // Local dev (authorizer disabled): identify the acting staff user for the audit log.
  if (!token && config.staffUsername) {
    headers['x-staff-username'] = config.staffUsername;
    headers['x-staff-email'] = config.staffEmail;
  }

  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let detail = res.statusText;
    try {
      const parsed = await res.json();
      detail = parsed?.message || parsed?.error || detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
